import { useEffect, useRef, useState } from "react";
import { PharmacyQuickAdd } from "./PharmacyQuickAdd";
import { Search, Store, Plus, Phone } from "lucide-react";

function Results({ testId, rows, value, onPick, onAddClick, close }) {
  return (
    <div data-testid={`${testId}-results`} className="absolute right-0 z-50 mt-1 max-h-72 w-full min-w-[260px] overflow-y-auto rounded-xl border border-[#EFEBE0] bg-white shadow-lg">
      {value && <button type="button" data-testid={`${testId}-clear`} onClick={() => { onPick(""); close(); }} className="w-full px-3 py-2 text-left text-xs font-semibold text-[#7B6157] hover:bg-[#F5F2EB]">Clear pharmacy</button>}
      {rows.map((p) => (
        <button key={p.id} type="button" data-testid={`${testId}-option-${p.id}`} onClick={() => { onPick(p.id); close(); }}
          className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-[#F5F2EB]">
          <Store size={13} className="mt-0.5 shrink-0 text-[#7B6157]" />
          <span className="min-w-0"><span className="block text-sm font-semibold text-[#3E2723]">{p.name}</span>
            <span className="block text-[11px] text-[#7B6157]">{[p.city, p.phone, p.email || "no email"].filter(Boolean).join(" · ")}</span></span>
        </button>
      ))}
      {!rows.length && <div className="px-3 py-3 text-xs text-[#84675B]">No pharmacy matches.</div>}
      {onAddClick && <button type="button" data-testid={`${testId}-add-new`} onClick={() => { close(); onAddClick(); }}
        className="flex w-full items-center gap-2 border-t border-[#F1EEE7] px-3 py-2.5 text-left text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB]"><Plus size={13} /> Add a new pharmacy</button>}
    </div>
  );
}

/** Searchable pharmacy picker over the Pharmacy Directory, with optional inline add and phone display. */
export function PharmacyPicker({ pharmacies, value, onPick, placeholder = "Search pharmacies", testId, onAdd, showContact, fallback }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const current = pharmacies.find((p) => p.id === value);
  const shown = current || fallback;
  const s = q.trim().toLowerCase();
  const rows = pharmacies.filter((p) => !s || [p.name, p.city, p.phone, p.email].some((x) => (x || "").toLowerCase().includes(s))).slice(0, 40);
  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <input data-testid={testId} value={open ? q : current ? current.name : ""} autoComplete="off" placeholder={current ? current.name : placeholder}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => { setQ(""); setOpen(true); }}
          className="h-11 w-full rounded-lg border border-[#E5DFD3] bg-white pl-9 pr-3 text-sm text-[#3E2723] outline-none focus:ring-2 focus:ring-[#8D6E63]" />
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
      </div>
      {open && <Results testId={testId} rows={rows} value={value} onPick={onPick} close={() => setOpen(false)} onAddClick={onAdd ? () => setAdding(true) : null} />}
      {(showContact || onAdd) && !adding && (
        <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px] text-[#7B6157]">
          {showContact && <span data-testid={`${testId}-phone`} className="flex items-center gap-1">{shown ? <><Phone size={10} /> {shown.phone || "No phone on file"}</> : "No pharmacy chosen"}</span>}
          {onAdd && <button type="button" data-testid={`${testId}-add-link`} onClick={() => setAdding(true)} className="font-semibold text-[#4E342E] underline">Add pharmacy</button>}
        </div>
      )}
      {adding && <PharmacyQuickAdd testId={`${testId}-quick-add`} onCancel={() => setAdding(false)} onAdded={(p) => { setAdding(false); onAdd(p); onPick(p.id); }} />}
    </div>
  );
}
